import { Link, useNavigate } from 'react-router';

import routes from '@/assets/constants/routes-no.ts';
import { EconomySection } from '@/components/marketing/economy-section/index.ts';
import { SplitHeroSection } from '@/components/marketing/split-hero-section/index.ts';
import { TestimonialsSection } from '@/components/marketing/testimonials-section/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { testimonialsQuery } from '@/lib/api/requests.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import { useAuth } from '@/services/Auth/index.ts';
import { LANG } from '@/services/Config.js';
import { Button } from '@/ui/button-2.tsx';

import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';

import type { Route } from './+types/property-value';
import backgroundSrc from './internal/background.avif';
import smartphoneSrc from './internal/smartphone.svg';
import { RRS_PROPERTY_VALUE } from './RRS.ts';
export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const testimonials = await queryClient.query(testimonialsQuery());
  return { testimonials };
}

export default function PropertyValue({ loaderData }: Route.ComponentProps) {
  const { appStrings } = LANG;
  const { login, isAnonymous } = useAuth();
  const navigate = useNavigate();
  const destination = `${routes.MY_SCOOPR_DASHBOARD}?from=property-value`;

  const ctaAction = async () => {
    sendUserPortalEvent('user_login_started');
    if (isAnonymous) {
      await login('SCRIVE', destination);
    } else {
      await navigate(destination);
    }
  };

  return (
    <Page title={appStrings.PAGES.PROPERTY_VALUE}>
      <RrsHandler {...RRS_PROPERTY_VALUE} />
      <div className="flex w-full flex-col bg-white">
        <article className="flex flex-col gap-8 sm:gap-15 md:mt-15">
          <SplitHeroSection.Root>
            <SplitHeroSection.Content>
              <SplitHeroSection.Title>
                Sjekk hva boligen
                <br />
                din er verdt
              </SplitHeroSection.Title>

              <SplitHeroSection.List>
                <SplitHeroSection.ListItem>
                  <span>
                    <b>Gratis</b> estimat av din boligverdi.
                  </span>
                </SplitHeroSection.ListItem>
                <SplitHeroSection.ListItem>
                  Se kredittscore og sjekk om du har betalingsanmerkninger.
                </SplitHeroSection.ListItem>
                <SplitHeroSection.ListItem>Se dine gjeldsopplysninger.</SplitHeroSection.ListItem>
              </SplitHeroSection.List>

              <Button color="yellow" className="mb-4 md:mb-6 lg:mb-8" action={ctaAction}>
                Logg på med BankID
              </Button>

              <p className="max-w-md text-center typography-v1-body3">
                Ved å fortsette aksepterer jeg Scoopr sine{' '}
                <Link
                  to={routes.TERMS_OF_USE_MIN_SCOOPR}
                  className="text-[#18A0FB] hover:underline"
                >
                  brukervilkår
                </Link>{' '}
                og{' '}
                <Link to={routes.PRIVACY_STATEMENT} className="text-[#18A0FB] hover:underline">
                  personvernerklæring
                </Link>
              </p>
            </SplitHeroSection.Content>

            <SplitHeroSection.ImageContainer className="max-sm:min-h-120">
              <img
                src={backgroundSrc}
                width={430}
                height={491}
                alt=""
                className="absolute top-0 left-0 -z-10 max-sm:max-w-3/4"
              />
              <SplitHeroSection.Image
                src={smartphoneSrc}
                width={220}
                height={250}
                alt="boligverdi"
              />
            </SplitHeroSection.ImageContainer>
          </SplitHeroSection.Root>
          <TestimonialsSection data={loaderData.testimonials} />
          <EconomySection />
        </article>
      </div>
    </Page>
  );
}
